import { useState, useCallback } from 'react';
import { CheckInFullResponse, Space } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';
import { adminCheckInUser, invalidateCheckInCaches } from '../../../services/api/endpoints/checkins';
import { fetchSpaces } from '../../../services/api/endpoints/spaces';

interface UseAdminCheckinOptions {
  onSuccess?: (response: CheckInFullResponse) => void;
  onError?: (error: string) => void;
}

interface UseAdminCheckinResult {
  // Check-in state
  isCheckingIn: boolean;
  checkInError: string | null;
  checkInSuccess: CheckInFullResponse | null;
  
  // Spaces state
  spaces: Space[];
  isLoadingSpaces: boolean;
  spacesError: string | null;
  
  // Actions
  checkInUser: (userId: string, spaceId: string, useDayPass?: boolean) => Promise<void>;
  loadSpaces: () => Promise<void>;
  clearState: () => void;
  invalidateCaches: (userId?: string) => void;
}

/**
 * Custom hook for managing admin check-in functionality
 * Handles API calls, loading states, error handling, and cache management
 */
export const useAdminCheckin = (options: UseAdminCheckinOptions = {}): UseAdminCheckinResult => {
  const { user } = useAuth();
  const { onSuccess, onError } = options;

  // Check-in state
  const [isCheckingIn, setIsCheckingIn] = useState<boolean>(false);
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [checkInSuccess, setCheckInSuccess] = useState<CheckInFullResponse | null>(null);

  // Spaces state
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [isLoadingSpaces, setIsLoadingSpaces] = useState<boolean>(false);
  const [spacesError, setSpacesError] = useState<string | null>(null);

  /**
   * Load available spaces for selection
   */
  const loadSpaces = useCallback(async (): Promise<void> => {
    if (!user?.token) {
      setSpacesError('Authentication required');
      return;
    }

    setIsLoadingSpaces(true);
    setSpacesError(null);

    try {
      const spacesData = await fetchSpaces(user.token);
      setSpaces(spacesData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load spaces';
      setSpacesError(errorMessage);
      console.error('Error loading spaces:', error);
    } finally {
      setIsLoadingSpaces(false);
    }
  }, [user?.token]);

  /**
   * Perform admin check-in for a user
   */
  const checkInUser = useCallback(async (
    userId: string,
    spaceId: string,
    useDayPass: boolean = false
  ): Promise<void> => {
    if (!user?.token) {
      const error = 'Authentication required';
      setCheckInError(error);
      if (onError) onError(error);
      return;
    }

    if (!userId || !spaceId) {
      const error = 'User ID and Space ID are required';
      setCheckInError(error);
      if (onError) onError(error);
      return;
    }

    setIsCheckingIn(true);
    setCheckInError(null);
    setCheckInSuccess(null);

    try {
      const response = await adminCheckInUser(user.token, userId, spaceId, useDayPass);

      if (response.status === 'success') {
        setCheckInSuccess(response);
        if (onSuccess) onSuccess(response);
      } else {
        // Handle non-success statuses with user-friendly messages
        const errorMessages: Record<string, string> = {
          already_checked_in: 'User has already checked in today at this space.',
          no_quota: `User has no available check-ins.${response.has_day_pass ? ' Try using a day pass.' : ' No day passes available.'}`,
          no_day_pass: 'User has no available day passes.',
          error: response.message || 'An error occurred during check-in.'
        };
        
        const errorMessage = errorMessages[response.status] || 'Unknown error occurred.';
        setCheckInError(errorMessage);
        if (onError) onError(errorMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check in user';
      setCheckInError(errorMessage);
      if (onError) onError(errorMessage);
      console.error('Error during check-in:', error);
    } finally {
      setIsCheckingIn(false);
    }
  }, [user?.token, onSuccess, onError]);

  /**
   * Clear all state (useful for modal close/reset)
   */
  const clearState = useCallback((): void => {
    setCheckInError(null);
    setCheckInSuccess(null);
    setSpacesError(null);
    // Keep spaces data and loading states as they can be reused
  }, []);

  /**
   * Invalidate check-in related caches
   */
  const invalidateCaches = useCallback((userId?: string): void => {
    invalidateCheckInCaches(userId);
  }, []);

  return {
    // Check-in state
    isCheckingIn,
    checkInError,
    checkInSuccess,
    
    // Spaces state
    spaces,
    isLoadingSpaces,
    spacesError,
    
    // Actions
    checkInUser,
    loadSpaces,
    clearState,
    invalidateCaches,
  };
};

export default useAdminCheckin;
